/**
 * Helpers for inbound notification toasts + notification center.
 */

import { Mail, MessageCircle, Send } from "lucide-react";

export const NOTIFICATIONS_CACHE_KEY = "sellix.notifications.cache";
export const NOTIFICATIONS_SEEN_KEY = "sellix.notifications.seenIds";
export const NOTIFICATIONS_POLL_MS = 10_000;

const CHANNEL_META = {
  email: {
    label: "Email",
    Icon: Mail,
    iconBg: "bg-rose-100 text-rose-700",
  },
  gmail: {
    label: "Email",
    Icon: Mail,
    iconBg: "bg-rose-100 text-rose-700",
  },
  whatsapp: {
    label: "WhatsApp",
    Icon: MessageCircle,
    iconBg: "bg-emerald-100 text-emerald-700",
  },
  telegram: {
    label: "Telegram",
    Icon: Send,
    iconBg: "bg-cyan-100 text-cyan-700",
  },
  facebook: {
    label: "Facebook",
    Icon: MessageCircle,
    iconBg: "bg-blue-100 text-blue-700",
  },
  instagram: {
    label: "Instagram",
    Icon: MessageCircle,
    iconBg: "bg-pink-100 text-pink-700",
  },
};

export function getChannelMeta(channel) {
  const key = (channel || "").toLowerCase();
  return (
    CHANNEL_META[key] || {
      label: (channel || "Message").replace(/^./, (c) => c.toUpperCase()),
      Icon: Mail,
      iconBg: "bg-slate-100 text-slate-700",
    }
  );
}

export function notificationTitle(n) {
  if (n?.title) return n.title;
  const name = (n?.sender_name || "").trim() || "Unknown";
  const ch = (n?.channel || "email").toLowerCase();
  if (ch === "email" || ch === "gmail") return `New Email received from ${name}`;
  if (ch === "whatsapp") return `New WhatsApp message from ${name}`;
  if (ch === "telegram") return `New Telegram message from ${name}`;
  if (ch === "facebook") return `New Facebook message from ${name}`;
  if (ch === "instagram") return `New Instagram message from ${name}`;
  return `New message from ${name}`;
}

export function computeUnreadNotificationCount(payload) {
  if (!payload) return 0;
  if (typeof payload.unread_count === "number") return payload.unread_count;
  const items = payload.notifications || [];
  return items.filter((n) => !n.is_read).length;
}

export function loadCachedNotifications(storage = sessionStorage) {
  try {
    const raw = storage.getItem(NOTIFICATIONS_CACHE_KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
}

export function cacheNotifications(data, storage = sessionStorage) {
  try {
    storage.setItem(NOTIFICATIONS_CACHE_KEY, JSON.stringify(data));
  } catch {
    // ignore quota / private browsing
  }
}

export function loadSeenNotificationIds(storage = sessionStorage) {
  try {
    const raw = storage.getItem(NOTIFICATIONS_SEEN_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return new Set(Array.isArray(parsed) ? parsed : []);
  } catch {
    return new Set();
  }
}

export function saveSeenNotificationIds(ids, storage = sessionStorage) {
  try {
    const list = [...ids].slice(-200);
    storage.setItem(NOTIFICATIONS_SEEN_KEY, JSON.stringify(list));
  } catch {
    // ignore
  }
}

/**
 * Diff new notifications against previously seen IDs for toast fan-out.
 * Returns notifications that should toast (newest first), and the updated seen set.
 */
export function diffNewNotifications(notifications, seenIds, { bootstrap = false } = {}) {
  const items = Array.isArray(notifications) ? notifications : [];
  const nextSeen = new Set(seenIds);
  if (bootstrap) {
    for (const n of items) {
      if (n?.id) nextSeen.add(n.id);
    }
    return { toToast: [], seenIds: nextSeen };
  }
  const toToast = [];
  for (const n of items) {
    if (!n?.id) continue;
    if (!nextSeen.has(n.id)) {
      toToast.push(n);
      nextSeen.add(n.id);
    }
  }
  // Newest first already from API; toast oldest-of-batch first so latest ends on top.
  return { toToast: toToast.reverse(), seenIds: nextSeen };
}

export function formatNotificationTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  const now = Date.now();
  const diffMs = now - d.getTime();
  const mins = Math.floor(diffMs / 60_000);
  if (mins < 1) return "Just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d ago`;
  return d.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}
